import React from 'react';
import { Alert, Box, Chip } from '@mui/material';
import { AutoAwesome, FolderOpen } from '@mui/icons-material';
import { filingLabel, filingState, unfiledReason } from '../../services/autopilotModel';
import FilingControls from './FilingControls';

/** DOM id of the banner (the recap email's "Change" link scrolls to it with ?focus=filing). */
export const FILING_BANNER_ID = 'autopilot-filing';

/**
 * Session page banner for autopilot's filing: "Filed under X > Y (new project) Change / Undo",
 * or why autopilot left the meeting unfiled (internal, plan limit, unsure) with "File it".
 * After Undo it reads "Moved back to unfiled" with "File it". Renders nothing when autopilot did
 * not file this meeting or it was moved since. The banner takes focus (tabIndex -1) when the
 * recap email's link lands on it.
 * @param {object} props - Props.
 * @param {object} props.session - Session ({id, name}).
 * @param {object|null} props.record - Normalized autopilot record.
 * @param {object|null} props.project - The session's project now (null when unfiled).
 * @param {object|null} props.customer - That project's customer.
 * @param {(event: {kind: string, message: string}) => void} [props.onChanged] - After Undo / Change.
 * @param {(project: {id: string, name: string}) => void} [props.onProjectInUse] - Undo kept a project in use (the page shows rename / merge).
 * @returns {JSX.Element|null} Banner.
 */
export default function FilingBanner({ session, record, project, customer, onChanged, onProjectInUse }) {
  const filing = record?.filing || null;
  if (!session || !filing) return null;
  const state = filingState(filing, project?.id || null);

  if (state === 'filed') {
    const { path, badge } = filingLabel(filing, { project, customer });
    return (
      <Alert
        id={FILING_BANNER_ID}
        tabIndex={-1}
        severity="success"
        icon={<FolderOpen fontSize="inherit" />}
        action={
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <FilingControls
              sessionId={session.id}
              sessionName={session.name}
              filing={filing}
              canUndo
              currentProjectId={project?.id || null}
              onChanged={onChanged}
              onProjectInUse={onProjectInUse}
            />
          </Box>
        }
        sx={{ alignItems: 'center', '& .MuiAlert-message': { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 } }}
      >
        <span>Filed under <strong>{path}</strong></span>
        {badge && <Chip size="small" icon={<AutoAwesome fontSize="inherit" />} label={`${badge}, auto-created`} />}
        {!badge && <Chip size="small" variant="outlined" label="filed by Autopilot" />}
      </Alert>
    );
  }

  const undone = state === 'undone';
  if (undone || (state === 'unfiled' && ['internal', 'limit', 'unfiled', 'skipped'].includes(filing.outcome))) {
    return (
      <Alert
        id={FILING_BANNER_ID}
        tabIndex={-1}
        severity={!undone && filing.outcome === 'limit' ? 'warning' : 'info'}
        action={
          <FilingControls
            sessionId={session.id}
            sessionName={session.name}
            filing={null}
            changeLabel="File it"
            onChanged={onChanged}
          />
        }
      >
        {undone ? 'Moved back to unfiled. File it under a project when you are ready.' : unfiledReason(filing)}
      </Alert>
    );
  }

  return null;
}
